import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Card, Col, Descriptions, Form, Input, Row, Space, Tag, Typography } from 'antd';
import { SaveOutlined, ThunderboltOutlined } from '@ant-design/icons';
import * as whatsappBroadcastService from '../../services/whatsappBroadcastService';
import { useAuth } from '../../contexts/AuthContext';

const { Title } = Typography;

interface WASettingItem {
  key: string;
  is_set: boolean;
  masked?: string | null;
  source?: string | null;
  updated_at?: string | null;
}

interface WASettingsData {
  mgmt_token: WASettingItem;
  waba_id: WASettingItem;
}

const renderStatus = (item?: WASettingItem) => {
  if (!item) return null;
  return (
    <Descriptions column={1} size="small" style={{ marginBottom: 0 }}>
      <Descriptions.Item label="Status">
        {item.is_set ? (
          <Tag color="green">Tersimpan (sumber: {item.source || 'db/env'})</Tag>
        ) : (
          <Tag color="default">Belum diset</Tag>
        )}
      </Descriptions.Item>
      {item.masked && (
        <Descriptions.Item label="Nilai">
          <code>{item.masked}</code>
        </Descriptions.Item>
      )}
      {item.updated_at && (
        <Descriptions.Item label="Diperbarui">{item.updated_at}</Descriptions.Item>
      )}
    </Descriptions>
  );
};

const Settings: React.FC = () => {
  const { message } = App.useApp();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm();

  const canManage = hasPermission('wa.template.manage');

  const [settings, setSettings] = useState<WASettingsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const r = await whatsappBroadcastService.getWASettings();
      setSettings(r.data || null);
    } catch (error) {
      console.error(error);
      message.error('Gagal memuat status pengaturan WhatsApp');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch (error) {
      return; // validation errors are rendered inline
    }

    const body: { mgmt_token?: string; waba_id?: string } = {};
    if (values.mgmt_token) body.mgmt_token = values.mgmt_token;
    if (values.waba_id) body.waba_id = values.waba_id;

    if (!body.mgmt_token && !body.waba_id) {
      message.error('Isi minimal salah satu field untuk disimpan');
      return;
    }

    setSaving(true);
    try {
      await whatsappBroadcastService.updateWASettings(body);
      message.success('Pengaturan WhatsApp berhasil disimpan');
      form.resetFields();
      await fetchSettings();
    } catch (error: any) {
      console.error(error);
      message.error(error?.response?.data?.message || 'Gagal menyimpan pengaturan WhatsApp');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await whatsappBroadcastService.testWAToken();
      if (res.status === 'success') {
        message.success(`${res.message} (${res.data?.templates_found ?? 0} template)`);
      } else {
        message.error(res.message || 'Token tidak valid');
      }
    } catch (error: any) {
      console.error(error);
      message.error(error?.response?.data?.message || 'Gagal menguji token WhatsApp');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Pengaturan WhatsApp
          </Title>
        </Col>
      </Row>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Token ini HANYA untuk sinkronisasi/kelola template ke Meta (bukan pengiriman pesan). Token Meta kedaluwarsa berkala — perbarui di sini kapan saja tanpa deploy ulang."
      />

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Card title="Management Token" loading={loading} bordered={false}>
            {renderStatus(settings?.mgmt_token)}
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="WABA ID" loading={loading} bordered={false}>
            {renderStatus(settings?.waba_id)}
          </Card>
        </Col>
      </Row>

      <Card bordered={false}>
        <Form form={form} layout="vertical">
          <Form.Item name="mgmt_token" label="Management Token (Bearer)">
            <Input.Password placeholder="Tempel token baru dari Meta" disabled={!canManage} autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="waba_id" label="WABA ID">
            <Input placeholder="WhatsApp Business Account ID" disabled={!canManage} />
          </Form.Item>
          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={!canManage}
              onClick={handleSave}
            >
              Simpan
            </Button>
            <Button icon={<ThunderboltOutlined />} loading={testing} disabled={!canManage} onClick={handleTest}>
              Test Token
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default Settings;
